import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams, Navigate } from 'react-router-dom';
import { FiArrowRight, FiCalendar, FiUser, FiPhone } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import blogPosts from '../data/blogPosts';
import { useReservation } from '../contexts/ReservationContext';
import './BlogPost.css';

const formatDate = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
};

// Convertit le markdown léger (**gras**, retours à la ligne) en JSX
const renderRich = (text) => {
  const lines = text.split('\n');
  return lines.map((line, lineIdx) => {
    const parts = line.split(/(\*\*[^*]+\*\*)/g);
    const content = parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i}>{part.slice(2, -2)}</strong>;
      }
      return part;
    });
    return (
      <React.Fragment key={lineIdx}>
        {content}
        {lineIdx < lines.length - 1 && <br />}
      </React.Fragment>
    );
  });
};

const BlogPost = () => {
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.slug === slug);
  const { open: openReservation } = useReservation();

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const phoneNumber = '33661630113';
  const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(
    `Bonjour, j'ai lu votre article "${post.title}" et je souhaite obtenir un devis.`
  )}`;

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://limousine-vip-paris.fr/" },
      { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://limousine-vip-paris.fr/blog" },
      { "@type": "ListItem", "position": 3, "name": post.title, "item": post.canonical }
    ]
  };

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "mainEntityOfPage": { "@type": "WebPage", "@id": post.canonical },
    "headline": post.title,
    "description": post.description,
    "image": `https://limousine-vip-paris.fr${post.image}`,
    "datePublished": post.publishedDate,
    "dateModified": post.modifiedDate || post.publishedDate,
    "author": { "@type": "Organization", "name": post.author, "url": "https://limousine-vip-paris.fr/" },
    "publisher": {
      "@type": "Organization",
      "name": "VIP Limousine Paris",
      "url": "https://limousine-vip-paris.fr/",
      "logo": {
        "@type": "ImageObject",
        "url": "https://limousine-vip-paris.fr/images/hero/hero-limousines-mariage-paris.webp"
      }
    },
    "keywords": post.keywords?.join(', ')
  };

  return (
    <article className="blog-post">
      <Helmet>
        <title>{post.title} | Blog VIP Limousine Paris</title>
        <meta name="description" content={post.description} />
        <link rel="canonical" href={post.canonical} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.description} />
        <meta property="og:url" content={post.canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={`https://limousine-vip-paris.fr${post.image}`} />
        <meta property="article:published_time" content={post.publishedDate} />
        <meta property="article:modified_time" content={post.modifiedDate || post.publishedDate} />
        <meta property="article:author" content={post.author} />

        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>

      <header className="blog-post-hero">
        <div className="blog-post-hero-image">
          <img
            src={post.image}
            alt={post.imageAlt}
            width="1600"
            height="1067"
            fetchpriority="high"
            loading="eager"
          />
          <div className="blog-post-hero-overlay" />
        </div>
        <div className="blog-post-hero-content container">
          <nav className="seo-breadcrumb" aria-label="Fil d'Ariane">
            <Link to="/">Accueil</Link>
            <span> › </span>
            <Link to="/blog">Blog</Link>
            <span> › </span>
            <span className="truncate">{post.title}</span>
          </nav>
          <h1 className="blog-post-title">{post.title}</h1>
          <div className="blog-post-meta">
            <span><FiCalendar size={14} /> {formatDate(post.publishedDate)}</span>
            <span><FiUser size={14} /> {post.author}</span>
          </div>
        </div>
      </header>

      <div className="blog-post-body container">
        <p className="blog-post-excerpt">{post.excerpt}</p>

        {post.sections.map((section, i) => (
          <section key={i} className="blog-post-section">
            <h2>{section.heading}</h2>
            <p>{renderRich(section.body)}</p>
          </section>
        ))}

        {post.cta && (
          <div className="blog-post-cta">
            <h3>Réservation</h3>
            <p>{post.cta}</p>
            <div className="blog-post-cta-buttons">
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
                <FaWhatsapp size={20} /> Devis WhatsApp
              </a>
              <a href={`tel:+${phoneNumber}`} className="btn btn-outline">
                <FiPhone size={18} /> 06 61 63 01 13
              </a>
              <button type="button" className="btn btn-outline" onClick={() => openReservation()}>
                Formulaire en ligne
              </button>
            </div>
          </div>
        )}

        {post.relatedPages && post.relatedPages.length > 0 && (
          <aside className="blog-post-related">
            <h3>Pages associées</h3>
            <div className="blog-post-related-grid">
              {post.relatedPages.map((r, i) => (
                <Link key={i} to={r.slug} className="blog-post-related-card">
                  <span>{r.title}</span>
                  <FiArrowRight />
                </Link>
              ))}
            </div>
          </aside>
        )}
      </div>
    </article>
  );
};

export default BlogPost;
